import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, Sparkles, Star } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { captureSource, getSignupSource, getRefCode } from "../lib/source";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tiers = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    period: "forever",
    description: "Start reading and join the community.",
    features: [
      "Access to all free articles",
      "Weekly newsletter",
      "Writing tips & resources",
      "Community comments",
    ],
    cta: "Subscribe Free",
    accent: false,
  },
  {
    id: "monthly",
    name: "Monthly",
    price: "$9",
    period: "per month",
    description: "Full access to everything Elizabeth writes.",
    features: [
      "Everything in Free",
      "Unlock all premium articles",
      "Exclusive in-depth guides",
      "Early access to new content",
      "Monthly writing workshop",
      "Direct email access",
    ],
    cta: "Start Monthly",
    accent: true,
    badge: "Most Popular",
  },
  {
    id: "yearly",
    name: "Yearly",
    price: "$90",
    period: "per year",
    description: "Best value — two months free.",
    features: [
      "Everything in Monthly",
      "2 months free vs monthly",
      "Annual writing strategy call",
      "Priority response",
      "Exclusive yearly bonuses",
    ],
    cta: "Start Yearly",
    accent: false,
    saving: "Save $18/year",
  },
];

export default function Subscribe() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [paidTier, setPaidTier] = useState(null);
  const [paidEmail, setPaidEmail] = useState("");
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    captureSource();
  }, []);

  const startCheckout = async (tierId, checkoutEmail) => {
    setRedirecting(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: tierId === "monthly" ? "premium_monthly" : "premium_yearly",
        origin_url: window.location.origin,
        email: checkoutEmail,
      });
      localStorage.setItem("fb_sub_email", checkoutEmail.toLowerCase());
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setRedirecting(false);
    }
  };

  const handlePaidClick = (tierId) => {
    const stored = localStorage.getItem("fb_sub_email") || email;
    if (stored) {
      startCheckout(tierId, stored);
    } else {
      setPaidTier(tierId);
    }
  };

  const handleFreeSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/subscribe`, { email, name, source: getSignupSource(), ref: getRefCode() });
      setSubscribed(true);
      localStorage.setItem("fb_sub_email", email.toLowerCase());
      toast.success("Welcome! You're now subscribed.");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-14"
          >
            <p className="overline mb-4">Plans & pricing</p>
            <h1 className="font-cormorant text-4xl sm:text-5xl font-semibold text-foreground mb-4">
              Choose your level of access
            </h1>
            <p className="font-manrope text-base text-muted-foreground max-w-xl mx-auto">
              Start free and upgrade when you're ready. Every plan includes Elizabeth's writing
              insights, tips, and newsletter.
            </p>
          </motion.div>

          {/* Tiers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            {tiers.map((tier, i) => (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.45 }}
                data-testid={`tier-${tier.id}`}
                className={`relative rounded-2xl border p-7 flex flex-col ${
                  tier.accent
                    ? "border-brand-accent bg-brand-accent/5 shadow-md"
                    : "border-border bg-card"
                }`}
              >
                {tier.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-brand-accent text-white text-xs font-semibold font-manrope">
                      <Star size={11} fill="white" />
                      {tier.badge}
                    </span>
                  </div>
                )}
                {tier.saving && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 rounded-full bg-green-500 text-white text-xs font-semibold font-manrope">
                      {tier.saving}
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <p className="overline text-brand-accent mb-2">{tier.name}</p>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="font-cormorant text-5xl font-bold text-foreground">{tier.price}</span>
                    <span className="font-manrope text-sm text-muted-foreground">/{tier.period}</span>
                  </div>
                  <p className="font-manrope text-sm text-muted-foreground">{tier.description}</p>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5">
                      <Check size={16} className="text-brand-accent shrink-0 mt-0.5" />
                      <span className="font-manrope text-sm text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                {tier.id === "free" ? (
                  subscribed ? (
                    <div className="space-y-2">
                      <div className="w-full py-3 rounded-full bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 font-manrope text-sm font-medium text-center">
                        You're subscribed!
                      </div>
                      <Link
                        to="/referrals"
                        data-testid="subscribe-referral-cta"
                        className="block text-center font-manrope text-xs text-brand-accent hover:underline"
                      >
                        Refer friends & earn free premium →
                      </Link>
                    </div>
                  ) : (
                    <form onSubmit={handleFreeSubscribe} className="space-y-2">
                      <input
                        type="text"
                        placeholder="Your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="subscribe-name-input"
                        className="w-full px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                      />
                      <input
                        type="email"
                        placeholder="Your email *"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="subscribe-email-input"
                        className="w-full px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                      />
                      <button
                        type="submit"
                        disabled={subscribing}
                        data-testid="subscribe-free-btn"
                        className="w-full py-3 rounded-full border border-brand-accent text-brand-accent font-manrope text-sm font-medium hover:bg-brand-accent hover:text-white disabled:opacity-60 transition-all duration-200"
                      >
                        {subscribing ? "Joining..." : tier.cta}
                      </button>
                    </form>
                  )
                ) : paidTier === tier.id ? (
                  <form
                    onSubmit={(e) => { e.preventDefault(); if (paidEmail) startCheckout(tier.id, paidEmail); }}
                    className="space-y-2"
                  >
                    <input
                      type="email"
                      required
                      autoFocus
                      placeholder="Your email for the membership"
                      value={paidEmail}
                      onChange={(e) => setPaidEmail(e.target.value)}
                      data-testid={`checkout-email-${tier.id}`}
                      className="w-full px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                    />
                    <button
                      type="submit"
                      disabled={redirecting}
                      data-testid={`checkout-continue-${tier.id}`}
                      className="w-full py-3 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover disabled:opacity-60 transition-colors"
                    >
                      {redirecting ? "Redirecting to checkout..." : "Continue to payment"}
                    </button>
                  </form>
                ) : (
                  <button
                    onClick={() => handlePaidClick(tier.id)}
                    disabled={redirecting}
                    data-testid={`subscribe-${tier.id}-btn`}
                    className={`w-full py-3 rounded-full font-manrope text-sm font-medium transition-colors duration-200 disabled:opacity-60 ${
                      tier.accent
                        ? "bg-brand-accent text-white hover:bg-brand-accent-hover"
                        : "border border-border text-foreground hover:bg-muted"
                    }`}
                  >
                    {redirecting ? "Redirecting..." : tier.cta}
                  </button>
                )}
              </motion.div>
            ))}
          </div>

          {/* FAQ */}
          <div className="border-t border-border pt-12">
            <h2 className="font-cormorant text-3xl font-semibold text-foreground mb-8 text-center">
              Frequently asked questions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  q: "What do free subscribers get?",
                  a: "Free subscribers get access to all free articles, the weekly newsletter, and can join the community discussion. It's a great way to start.",
                },
                {
                  q: "What's included in paid plans?",
                  a: "Paid subscribers unlock every premium post — deep-dive guides, industry analysis, and exclusive writing resources that go beyond the free content.",
                },
                {
                  q: "Can I cancel anytime?",
                  a: "Absolutely. You can cancel your subscription at any time. We believe in earning your loyalty, not locking you in.",
                },
                {
                  q: "Is this different from the main site?",
                  a: "Yes! Freelanceblogger.club is Elizabeth's portfolio and pricing page. This newsletter platform is where she publishes ongoing content and insights.",
                },
              ].map((item) => (
                <div key={item.q}>
                  <h3 className="font-manrope text-sm font-semibold text-foreground mb-2">{item.q}</h3>
                  <p className="font-manrope text-sm text-muted-foreground leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
